import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import PageFooter from '@/components/feature/PageFooter';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
const ERP_ENDPOINT = `${SUPABASE_URL}/functions/v1/erp-points`;

export default function ErpGuidePage() {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [testId, setTestId] = useState('MEM001');
  const [testPoints, setTestPoints] = useState('1000');
  const [testResult, setTestResult] = useState<{ success: boolean; msg: string; data?: object } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [activeTab, setActiveTab] = useState<'guide' | 'test' | 'csv'>('guide');

  const copy = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleLiveTest = async () => {
    if (!testId.trim() || !testPoints.trim()) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(ERP_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_KEY}`,
        },
        body: JSON.stringify({
          user_code: testId.trim().toUpperCase(),
          price: parseInt(testPoints, 10),
          mode: 'save',
        }),
      });
      const data = await res.json();
      setTestResult({ success: data.success === 1, msg: data.msg, data });
    } catch {
      setTestResult({ success: false, msg: '서버 연결에 실패했습니다. 네트워크를 확인해주세요.' });
    } finally {
      setIsTesting(false);
    }
  };

  const curlExample = `curl -X POST "${ERP_ENDPOINT}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${SUPABASE_KEY}" \\
  -d '{
    "user_code": "MEM001",
    "price": 5000,
    "mode": "save"
  }'`;

  const jsonExample = JSON.stringify({
    user_code: "MEM001",
    price: 5000,
    mode: "save"
  }, null, 2);

  const successResponse = JSON.stringify({
    success: 1,
    msg: "홍길동(MEM001) 회원에게 5,000P가 적립되었습니다.",
    member_id: "MEM001",
    member_name: "홍길동",
    new_balance: 505000
  }, null, 2);

  const errorResponse = JSON.stringify({
    success: 0,
    msg: "등록되지 않은 회원ID입니다. (MEM999) — members 테이블에 먼저 등록해주세요."
  }, null, 2);

  const csvTemplate = `member_id,member_name,points_balance\nMEM001,홍길동,500000\nMEM002,김철수,150000\nMEM003,이영희,320000`;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">

          {/* 헤더 */}
          <div className="py-8 md:py-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-xl">
                <i className="ri-links-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div>
                <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest">Internal</p>
                <h1 className="text-xl md:text-2xl font-black text-gray-900" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                  ERP 포인트 연동 가이드
                </h1>
              </div>
            </div>
            <p className="text-sm text-gray-500 leading-relaxed">
              ERP 전산에서 쇼핑몰로 포인트를 전송하는 방법을 안내합니다. 담당자는 이 문서를 참고하여 연동 작업을 진행해주세요.
            </p>
          </div>

          {/* ★ return_page 변경 공지 배너 ★ */}
          <div className="mb-6 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-5 md:p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                <i className="ri-notification-3-fill text-white text-xl w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-black bg-white/20 text-white border border-white/30">
                    2026.04.08 업데이트
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-black bg-red-400/80 text-white">
                    전산 담당자 필독
                  </span>
                </div>
                <h3 className="text-base md:text-lg font-black mb-2">
                  return_page 주소가 변경되었습니다
                </h3>
                <p className="text-emerald-100 text-sm leading-relaxed mb-4">
                  ERP 전산 프로그램에서 포인트 조회 후 결과를 쇼핑몰로 전달하는 <strong className="text-white">return_page</strong> 주소가 아래와 같이 변경되었습니다.
                  전산 담당자분께서는 ERP 프로그램 내 해당 설정을 반드시 업데이트해 주세요.
                </p>

                {/* 변경 전/후 비교 */}
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <div className="bg-red-500/20 border border-red-400/40 rounded-xl p-3">
                    <p className="text-xs font-bold text-red-200 mb-1.5 flex items-center gap-1">
                      <i className="ri-close-circle-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      변경 전 (사용 불가)
                    </p>
                    <code className="text-xs font-mono text-red-200 break-all line-through opacity-70">
                      https://쇼핑몰사이트주소/결과값을 받을 페이지주소
                    </code>
                  </div>
                  <div className="bg-white/15 border border-white/30 rounded-xl p-3">
                    <p className="text-xs font-bold text-emerald-200 mb-1.5 flex items-center gap-1">
                      <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      변경 후 (현재 적용)
                    </p>
                    <code className="text-xs font-mono text-white break-all select-all">
                      https://readdy.ai/project/fab39877-057d-4165-9475-a35374cb4014/erp-callback
                    </code>
                  </div>
                </div>

                {/* 복사 버튼 */}
                <button
                  onClick={() => copy('https://readdy.ai/project/fab39877-057d-4165-9475-a35374cb4014/erp-callback', 'return_page')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    copiedField === 'return_page'
                      ? 'bg-white text-emerald-700'
                      : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'
                  }`}
                >
                  {copiedField === 'return_page' ? (
                    <><i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>복사 완료!</>
                  ) : (
                    <><i className="ri-file-copy-line w-4 h-4 flex items-center justify-center"></i>새 주소 복사하기</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* 상태 뱃지 */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
            <div className="bg-white rounded-xl p-4 border border-emerald-100">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-bold text-emerald-600">운영 중</span>
              </div>
              <p className="text-sm font-bold text-gray-800">API 서버</p>
              <p className="text-xs text-gray-400 mt-0.5">정상 응답 중</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-id-card-line text-gray-500 w-4 h-4 flex items-center justify-center"></i>
                <span className="text-xs font-bold text-gray-600">ID 형식</span>
              </div>
              <p className="text-sm font-bold text-gray-800">MEM001 형식</p>
              <p className="text-xs text-gray-400 mt-0.5">문자 + 숫자 혼합</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100 col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-database-2-line text-gray-500 w-4 h-4 flex items-center justify-center"></i>
                <span className="text-xs font-bold text-gray-600">등록 회원</span>
              </div>
              <p className="text-sm font-bold text-gray-800">MEM001 ~ MEM005</p>
              <p className="text-xs text-gray-400 mt-0.5">테스트 회원 등록 완료</p>
            </div>
          </div>

          {/* 탭 */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6">
            {([
              { id: 'guide', label: '연동 가이드', icon: 'ri-book-open-line' },
              { id: 'test', label: '라이브 테스트', icon: 'ri-flask-line' },
              { id: 'csv', label: 'CSV 업로드', icon: 'ri-file-excel-line' },
            ] as const).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white text-emerald-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={`${tab.icon} w-4 h-4 flex items-center justify-center`}></i>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* ─── 탭 1: 연동 가이드 ─── */}
          {activeTab === 'guide' && (
            <div className="space-y-5">

              {/* STEP 1: 개요 */}
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 flex items-center justify-center bg-emerald-600 rounded-full text-white text-xs font-black">1</div>
                  <h2 className="text-base font-bold text-gray-900">API 엔드포인트 확인</h2>
                </div>
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                  ERP에서 아래 URL로 <strong>HTTP POST</strong> 요청을 보내면 포인트가 자동으로 적립됩니다.
                </p>
                <div className="bg-slate-900 rounded-xl p-4 flex items-center gap-3">
                  <span className="text-xs font-bold text-emerald-400 bg-emerald-900/40 px-2 py-1 rounded-md flex-shrink-0">POST</span>
                  <code className="text-sm text-slate-200 font-mono flex-1 break-all">{ERP_ENDPOINT}</code>
                  <button
                    onClick={() => copy(ERP_ENDPOINT, 'endpoint')}
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors cursor-pointer"
                  >
                    <i className={`${copiedField === 'endpoint' ? 'ri-check-line text-emerald-400' : 'ri-file-copy-line text-slate-300'} w-4 h-4 flex items-center justify-center text-sm`}></i>
                  </button>
                </div>
              </div>

              {/* STEP 2: 요청 파라미터 */}
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 flex items-center justify-center bg-emerald-600 rounded-full text-white text-xs font-black">2</div>
                  <h2 className="text-base font-bold text-gray-900">요청 파라미터 (JSON Body)</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 pr-4 text-xs font-bold text-gray-500 uppercase">파라미터</th>
                        <th className="text-left py-2 pr-4 text-xs font-bold text-gray-500 uppercase">타입</th>
                        <th className="text-left py-2 pr-4 text-xs font-bold text-gray-500 uppercase">필수</th>
                        <th className="text-left py-2 text-xs font-bold text-gray-500 uppercase">설명</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      <tr>
                        <td className="py-3 pr-4"><code className="text-xs font-mono font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">user_code</code></td>
                        <td className="py-3 pr-4 text-xs text-gray-500">string</td>
                        <td className="py-3 pr-4">
                          <span className="text-xs font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">필수</span>
                        </td>
                        <td className="py-3 text-xs text-gray-600">회원 ID (예: MEM001, MEM002)</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4"><code className="text-xs font-mono font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">price</code></td>
                        <td className="py-3 pr-4 text-xs text-gray-500">number</td>
                        <td className="py-3 pr-4">
                          <span className="text-xs font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">필수</span>
                        </td>
                        <td className="py-3 text-xs text-gray-600">적립할 포인트 금액 (정수, 양수)</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4"><code className="text-xs font-mono font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">mode</code></td>
                        <td className="py-3 pr-4 text-xs text-gray-500">string</td>
                        <td className="py-3 pr-4">
                          <span className="text-xs font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">필수</span>
                        </td>
                        <td className="py-3 text-xs text-gray-600">동작 모드 → 적립 시 반드시 <code className="font-mono font-bold">&quot;save&quot;</code></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* 헤더 안내 */}
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-xs font-bold text-amber-800 mb-1.5 flex items-center gap-1.5">
                    <i className="ri-information-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    HTTP 헤더 필수 설정
                  </p>
                  <div className="space-y-1 font-mono">
                    <p className="text-xs text-amber-700"><span className="text-amber-500">Content-Type:</span> application/json</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-amber-700 flex-1 truncate"><span className="text-amber-500">Authorization:</span> Bearer {SUPABASE_KEY.slice(0, 30)}...</p>
                      <button
                        onClick={() => copy(`Bearer ${SUPABASE_KEY}`, 'auth')}
                        className="flex-shrink-0 text-xs font-bold text-amber-600 bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded-md cursor-pointer whitespace-nowrap transition-colors"
                      >
                        {copiedField === 'auth' ? '복사됨!' : '전체 복사'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* STEP 3: 요청 예시 */}
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 flex items-center justify-center bg-emerald-600 rounded-full text-white text-xs font-black">3</div>
                  <h2 className="text-base font-bold text-gray-900">요청 예시 코드</h2>
                </div>

                <p className="text-xs font-bold text-gray-500 uppercase mb-2">cURL</p>
                <div className="bg-slate-900 rounded-xl p-4 relative mb-4">
                  <button
                    onClick={() => copy(curlExample, 'curl')}
                    className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-slate-700 hover:bg-slate-600 rounded-lg cursor-pointer transition-colors"
                  >
                    <i className={`${copiedField === 'curl' ? 'ri-check-line text-emerald-400' : 'ri-file-copy-line text-slate-300'} w-3.5 h-3.5 flex items-center justify-center text-sm`}></i>
                  </button>
                  <pre className="text-xs text-slate-200 font-mono overflow-x-auto leading-relaxed">{curlExample}</pre>
                </div>

                <p className="text-xs font-bold text-gray-500 uppercase mb-2">JSON 요청 본문</p>
                <div className="bg-slate-900 rounded-xl p-4 relative">
                  <button
                    onClick={() => copy(jsonExample, 'json')}
                    className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-slate-700 hover:bg-slate-600 rounded-lg cursor-pointer transition-colors"
                  >
                    <i className={`${copiedField === 'json' ? 'ri-check-line text-emerald-400' : 'ri-file-copy-line text-slate-300'} w-3.5 h-3.5 flex items-center justify-center text-sm`}></i>
                  </button>
                  <pre className="text-xs text-slate-200 font-mono leading-relaxed">{jsonExample}</pre>
                </div>
              </div>

              {/* STEP 4: 응답 포맷 */}
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 flex items-center justify-center bg-emerald-600 rounded-full text-white text-xs font-black">4</div>
                  <h2 className="text-base font-bold text-gray-900">응답 포맷</h2>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <p className="text-xs font-bold text-emerald-700">성공 응답 (success: 1)</p>
                    </div>
                    <div className="bg-slate-900 rounded-xl p-4">
                      <pre className="text-xs text-emerald-300 font-mono leading-relaxed">{successResponse}</pre>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <p className="text-xs font-bold text-red-600">실패 응답 (success: 0)</p>
                    </div>
                    <div className="bg-slate-900 rounded-xl p-4">
                      <pre className="text-xs text-red-300 font-mono leading-relaxed">{errorResponse}</pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* 주의사항 */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                <p className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-1.5">
                  <i className="ri-alert-line w-4 h-4 flex items-center justify-center"></i>
                  연동 시 주의사항
                </p>
                <ul className="space-y-2 text-xs text-amber-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-amber-600"></i>
                    회원 ID는 반드시 <strong>대문자</strong>로 전송 (mem001 → MEM001 자동 변환되나 대문자 권장)
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-amber-600"></i>
                    members 테이블에 <strong>사전 등록된 회원만</strong> 포인트 적립 가능 (미등록 시 오류 반환)
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-amber-600"></i>
                    <strong>mode는 반드시 &quot;save&quot;</strong>로 설정
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-amber-600"></i>
                    price는 <strong>정수 (소수점 없음)</strong>로 전송
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-amber-600"></i>
                    응답의 <strong>success 값이 1이어야 적립 성공</strong> (0은 실패)
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* ─── 탭 2: 라이브 테스트 ─── */}
          {activeTab === 'test' && (
            <div className="space-y-5">
              <div className="bg-white rounded-2xl p-6">
                <h2 className="text-base font-bold text-gray-900 mb-1">실시간 API 테스트</h2>
                <p className="text-xs text-gray-500 mb-5">실제 API를 직접 호출하여 포인트 적립을 테스트합니다. 테스트 회원 MEM001~MEM005를 사용해보세요.</p>

                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">
                        회원 ID (user_code)
                        <span className="ml-1 text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={testId}
                        onChange={(e) => setTestId(e.target.value.toUpperCase())}
                        placeholder="MEM001"
                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-400 uppercase"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">
                        적립 포인트 (price)
                        <span className="ml-1 text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={testPoints}
                        onChange={(e) => setTestPoints(e.target.value)}
                        placeholder="1000"
                        min="1"
                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      />
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                    <p className="text-xs font-bold text-slate-600 mb-1.5">전송될 요청 미리보기</p>
                    <pre className="text-xs font-mono text-slate-700 leading-relaxed">{JSON.stringify({ user_code: testId || 'MEM001', price: parseInt(testPoints) || 1000, mode: 'save' }, null, 2)}</pre>
                  </div>

                  <button
                    onClick={handleLiveTest}
                    disabled={isTesting || !testId.trim() || !testPoints.trim()}
                    className="w-full py-3 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {isTesting ? (
                      <><i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>API 호출 중...</>
                    ) : (
                      <><i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>포인트 적립 테스트 실행</>
                    )}
                  </button>

                  {testResult && (
                    <div className={`rounded-xl p-4 border-2 ${testResult.success ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full ${testResult.success ? 'bg-emerald-500' : 'bg-red-500'}`}>
                          <i className={`${testResult.success ? 'ri-check-line' : 'ri-close-line'} text-white w-3 h-3 flex items-center justify-center text-xs`}></i>
                        </div>
                        <p className={`text-sm font-bold ${testResult.success ? 'text-emerald-800' : 'text-red-800'}`}>
                          {testResult.success ? '적립 성공!' : '오류 발생'}
                        </p>
                      </div>
                      <p className={`text-xs mb-3 ${testResult.success ? 'text-emerald-700' : 'text-red-700'}`}>{testResult.msg}</p>
                      {testResult.data && (
                        <div className="bg-slate-900 rounded-lg p-3">
                          <p className="text-xs text-slate-400 mb-1 font-bold">응답 JSON</p>
                          <pre className="text-xs font-mono text-slate-200 overflow-x-auto leading-relaxed">{JSON.stringify(testResult.data, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 등록된 테스트 회원 목록 */}
              <div className="bg-white rounded-2xl p-6">
                <h2 className="text-base font-bold text-gray-900 mb-4">테스트 회원 목록</h2>
                <div className="space-y-2">
                  {[
                    { id: 'MEM001', name: '홍길동', balance: 500000 },
                    { id: 'MEM002', name: '김철수', balance: 150000 },
                    { id: 'MEM003', name: '이영희', balance: 320000 },
                    { id: 'MEM004', name: '박민준', balance: 80000 },
                    { id: 'MEM005', name: '최수진', balance: 1200000 },
                  ].map((m) => (
                    <div key={m.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-lg">
                          <span className="text-xs font-black text-emerald-700">{m.name[0]}</span>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{m.name}</p>
                          <p className="text-xs font-mono text-emerald-600">{m.id}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-black text-gray-800">{m.balance.toLocaleString()}P</p>
                        <button
                          onClick={() => setTestId(m.id)}
                          className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold cursor-pointer"
                        >
                          이 ID로 테스트
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── 탭 3: CSV 업로드 ─── */}
          {activeTab === 'csv' && (
            <div className="space-y-5">

              <div className="bg-white rounded-2xl p-6">
                <h2 className="text-base font-bold text-gray-900 mb-1">회원 데이터 CSV 업로드</h2>
                <p className="text-xs text-gray-500 mb-5 leading-relaxed">
                  실제 회원 포인트 데이터를 Supabase에 업로드하는 방법입니다. 아래 형식에 맞게 CSV 파일을 준비해주세요.
                </p>

                {/* CSV 형식 */}
                <div className="mb-5">
                  <p className="text-xs font-bold text-gray-700 mb-2">CSV 파일 형식</p>
                  <div className="bg-slate-900 rounded-xl p-4 relative">
                    <button
                      onClick={() => copy(csvTemplate, 'csv')}
                      className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-slate-700 hover:bg-slate-600 rounded-lg cursor-pointer transition-colors"
                    >
                      <i className={`${copiedField === 'csv' ? 'ri-check-line text-emerald-400' : 'ri-file-copy-line text-slate-300'} w-3.5 h-3.5 flex items-center justify-center text-sm`}></i>
                    </button>
                    <pre className="text-xs text-slate-200 font-mono leading-relaxed">{csvTemplate}</pre>
                  </div>
                </div>

                {/* 컬럼 설명 */}
                <div className="mb-5">
                  <p className="text-xs font-bold text-gray-700 mb-2">컬럼 설명</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 rounded-lg">
                          <th className="text-left py-2 px-3 text-xs font-bold text-gray-500">컬럼명</th>
                          <th className="text-left py-2 px-3 text-xs font-bold text-gray-500">타입</th>
                          <th className="text-left py-2 px-3 text-xs font-bold text-gray-500">설명</th>
                          <th className="text-left py-2 px-3 text-xs font-bold text-gray-500">예시</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        <tr>
                          <td className="py-2.5 px-3"><code className="text-xs font-mono font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">member_id</code></td>
                          <td className="py-2.5 px-3 text-xs text-gray-500">text (PK)</td>
                          <td className="py-2.5 px-3 text-xs text-gray-600">회원 고유 ID — 대문자</td>
                          <td className="py-2.5 px-3 text-xs font-mono text-gray-700">MEM001</td>
                        </tr>
                        <tr>
                          <td className="py-2.5 px-3"><code className="text-xs font-mono font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">member_name</code></td>
                          <td className="py-2.5 px-3 text-xs text-gray-500">text</td>
                          <td className="py-2.5 px-3 text-xs text-gray-600">회원 이름</td>
                          <td className="py-2.5 px-3 text-xs font-mono text-gray-700">홍길동</td>
                        </tr>
                        <tr>
                          <td className="py-2.5 px-3"><code className="text-xs font-mono font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">points_balance</code></td>
                          <td className="py-2.5 px-3 text-xs text-gray-500">integer</td>
                          <td className="py-2.5 px-3 text-xs text-gray-600">현재 포인트 잔액 (숫자만)</td>
                          <td className="py-2.5 px-3 text-xs font-mono text-gray-700">150000</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 업로드 단계 */}
                <div className="bg-slate-50 rounded-xl p-5">
                  <p className="text-xs font-bold text-gray-700 mb-3">Supabase 업로드 방법 (단계별)</p>
                  <ol className="space-y-4">
                    {[
                      { step: '1', title: 'Supabase 대시보드 접속', desc: 'supabase.com/dashboard 에서 이 프로젝트로 이동합니다.', icon: 'ri-external-link-line' },
                      { step: '2', title: 'Table Editor → members 선택', desc: '좌측 메뉴에서 "Table Editor" 클릭 → "members" 테이블 선택합니다.', icon: 'ri-database-2-line' },
                      { step: '3', title: 'CSV Import 실행', desc: '우측 상단 "Insert" 버튼 옆 화살표(▼) → "Import data from CSV" 클릭합니다.', icon: 'ri-upload-cloud-line' },
                      { step: '4', title: 'CSV 파일 업로드 및 매핑 확인', desc: '준비한 CSV 파일을 업로드하고, 컬럼 매핑이 올바른지 확인합니다.', icon: 'ri-table-line' },
                      { step: '5', title: 'Import 완료 → 테스트', desc: '"Import" 버튼 클릭 후 완료! 이 페이지의 "라이브 테스트"에서 바로 확인 가능합니다.', icon: 'ri-checkbox-circle-line' },
                    ].map((item) => (
                      <li key={item.step} className="flex items-start gap-3">
                        <div className="w-7 h-7 flex items-center justify-center bg-emerald-600 rounded-full text-white text-xs font-black flex-shrink-0 mt-0.5">{item.step}</div>
                        <div>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <i className={`${item.icon} text-emerald-600 w-3.5 h-3.5 flex items-center justify-center text-sm`}></i>
                            <p className="text-sm font-bold text-gray-800">{item.title}</p>
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              {/* 주의사항 */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
                <p className="text-sm font-bold text-emerald-800 mb-3 flex items-center gap-1.5">
                  <i className="ri-lightbulb-line w-4 h-4 flex items-center justify-center"></i>
                  CSV 준비 시 주의사항
                </p>
                <ul className="space-y-1.5 text-xs text-emerald-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                    파일 인코딩은 <strong>UTF-8</strong>로 저장하세요 (한글 깨짐 방지)
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                    member_id는 <strong>중복 불가</strong> — 기존 ID와 겹치면 업데이트됩니다
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                    points_balance에 <strong>쉼표, 원 기호 없이</strong> 숫자만 입력 (150,000 ❌ → 150000 ✅)
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                    첫 번째 행은 반드시 <strong>컬럼명 헤더</strong>
                  </li>
                </ul>
              </div>
            </div>
          )}

        </div>
      </div>
      <PageFooter />
    </>
  );
}
